import { flow, isUndefined, set } from 'lodash/fp';
import { Reducer } from 'redux';

import { LoginActionTypes } from '../../../../auth/login/store/types';
import { DEFAULT_PAGE_SIZE } from '../../../../constants/arkTable';
import { AnalysisView } from '../../../../documents/store/types';
import { AdminAIPlaygroundActionTypes, AdminAIPlaygroundState, AIPlaygroundTab, PlaygroundQuestion, PreviousQuestionsTab } from './types';

export const INITIAL_STATE: AdminAIPlaygroundState = {
    isFetchingMLTestDocuments: false,
    mlTestDocuments: [],
    mlTestDocumentsTotal: 0,
    mlDocumentsTableFilters: {},
    mlDocumentsTablePageNumber: 1,
    mlDocumentsTableColumnSort: undefined,
    mlDocumentsTablePageSize: DEFAULT_PAGE_SIZE,
    mlTestDocumentsUploadModalOpen: false,
    isUploadingMLTestDocument: false,
    filesToUpload: null,
    fileDocumentNameIds: [],
    isOpening: false,
    mlTestDocumentUrl: null,
    mlTestDocument: null,
    mlTestDocumentAnalysisView: AnalysisView.SPLIT,
    aiPlaygroundSearchTerm: '',
    selectedPage: null,
    selectedQueries: [],
    currentSelectedQueries: [],
    currentCorrectQueries: [],
    confirmationModalVisible: false,
    isAskingAIPlaygroundQuestions: false,
    aiResults: null,
    mlTestDocumentExtractId: null,
    isPlaygroundSection: false,
    isFilteringQuerySelection: false,
    selectedAIPlaygroundTab: AIPlaygroundTab.ACTIVE_AI_QUESTIONS,
    proposedAIPlaygroundTab: null,
    isFetchingAllPreviousQuestionsAndAnswers: false,
    allPreviousQuestionsAndAnswers: null,
    previouslyAskedQueriesSearchTerm: '',
    selectedPreviousQuestionsTab: PreviousQuestionsTab.CORRECT
};

export const adminAIPlaygroundReducer: Reducer<AdminAIPlaygroundState> = (state = INITIAL_STATE, { payload, type }): AdminAIPlaygroundState => {
    switch (type) {
        case AdminAIPlaygroundActionTypes.FETCH_ML_TEST_DOCUMENTS_STARTED:
            return set('isFetchingMLTestDocuments', true, state);
        case AdminAIPlaygroundActionTypes.FETCH_ML_TEST_DOCUMENTS_SUCCESSFUL: {
            const { documents, total, pageNumber } = payload;
            return flow(
                set('isFetchingMLTestDocuments', false),
                set('mlTestDocuments', documents),
                set('mlTestDocumentsTotal', total),
                set('mlDocumentsTablePageNumber', pageNumber || state.mlDocumentsTablePageNumber),
            )(state);
        }
        case AdminAIPlaygroundActionTypes.FETCH_ML_TEST_DOCUMENTS_FAILED:
            return set('isFetchingMLTestDocuments', false, state);
        case AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_TABLE_FILTERS: {
            const isTextFilter = payload.type === 'text';
            return flow(
                set(`mlDocumentsTableFilters[${payload.key}][${payload.type}]`, payload.value),
                set(`mlDocumentsTableFilters[${payload.key}][${isTextFilter ? 'dropdown' : 'text'}]`, isTextFilter ? null : '')
            )(state);
        }
        case AdminAIPlaygroundActionTypes.CLEAR_ML_TEST_DOCUMENTS_TABLE_FILTERS:
            return set('mlDocumentsTableFilters', {}, state);
        case AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_TABLE_COLUMN_SORT:
            return set('mlDocumentsTableColumnSort', payload, state);
        case AdminAIPlaygroundActionTypes.SET_ML_TEST_DOCUMENTS_PAGE_SIZE:
            return set('mlDocumentsTablePageSize', payload, state);
        case AdminAIPlaygroundActionTypes.UPLOAD_ML_TEST_DOCUMENT_MODAL_OPEN: {
            const { value } = payload;
            return flow(
                set('mlTestDocumentsUploadModalOpen', value),
                set('filesToUpload', null)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.UPLOAD_ML_TEST_DOCUMENT_STARTED:
            return set('isUploadingMLTestDocument', true, state);
        case AdminAIPlaygroundActionTypes.UPLOAD_ML_TEST_DOCUMENT_SUCCESSFUL:
            return flow(
                set('isUploadingMLTestDocument', false),
                set('mlTestDocumentsUploadModalOpen', false),
                set('filesToUpload', null)
            )(state);
        case AdminAIPlaygroundActionTypes.UPLOAD_ML_TEST_DOCUMENT_FAILED:
            return set('isUploadingMLTestDocument', false, state);
        case AdminAIPlaygroundActionTypes.SET_INITIAL_UPLOAD_ML_TEST_DOCUMENTS: {
            const fileDocumentNameIds = (payload as File[]).map(({ name }) => ({ documentDescription: name, documentNameId: null }));
            return flow(
                set('filesToUpload', payload),
                set('fileDocumentNameIds', fileDocumentNameIds)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.UPDATE_ML_TEST_DOCUMENT_NAME: {
            const { value, documentDescription } = payload;
            const fileToUpdate = state.fileDocumentNameIds.findIndex(document => document.documentDescription === documentDescription);
            return set(`fileDocumentNameIds[${fileToUpdate}].documentNameId`, value, state);
        }
        case AdminAIPlaygroundActionTypes.OPEN_ML_TEST_DOCUMENT_STARTED:
            return flow(
                set('isOpening', true),
                set('isPlaygroundSection', true)
            )(state);
        case AdminAIPlaygroundActionTypes.OPEN_ML_TEST_DOCUMENT_SUCCESSFUL: {
            const { mlTestDocument, mlTestDocumentUrl } = payload;
            return flow(
                set('isOpening', false),
                set('mlTestDocumentUrl', mlTestDocumentUrl),
                set('mlTestDocument', mlTestDocument)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.OPEN_ML_TEST_DOCUMENT_FAILED:
            return set('isOpening', false, state);
        case AdminAIPlaygroundActionTypes.TOGGLE_ML_TEST_DOCUMENT_ANALYSIS_VIEW:
            return set('mlTestDocumentAnalysisView', payload, state);
        case AdminAIPlaygroundActionTypes.RESET_ML_TEST_DOCUMENT:
            return flow(
                set('mlTestDocumentUrl', null),
                set('mlTestDocument', null),
                set('selectedPage', null),
                set('aiPlaygroundSearchTerm', ''),
                set('selectedQueries', []),
                set('currentSelectedQueries', []),
                set('confirmationModalVisible', false),
                set('isPlaygroundSection', false),
                set('aiResults', null),
                set('selectedAIPlaygroundTab', AIPlaygroundTab.ACTIVE_AI_QUESTIONS),
                set('previouslyAskedQueriesSearchTerm', ''),
                set('selectedPreviousQuestionsTab', PreviousQuestionsTab.CORRECT)
            )(state);
        case AdminAIPlaygroundActionTypes.SCHEDULE_START_AND_END_PAGE_UPDATE_VALUE: {
            const { key, value } = payload;
            return set(`mlTestDocument[${key}]`, value, state);
        }
        case AdminAIPlaygroundActionTypes.SET_SCHEDULE_PAGE:
            return set('selectedPage', payload, state);
        case AdminAIPlaygroundActionTypes.TOGGLE_PLAYGROUND_QUESTION: {
            const playgroundQuery: PlaygroundQuestion = payload;
            let selectedQueries = state.selectedQueries;
            const alreadyIncluded = selectedQueries.map(({ queryKey }) => queryKey).includes(payload.queryKey);
            if (alreadyIncluded) {
                selectedQueries = selectedQueries.filter(({ queryKey }) => queryKey !== payload.queryKey);
            } else {
                selectedQueries = [...selectedQueries, playgroundQuery];
            }
            return flow(
                set('selectedQueries', selectedQueries),
                set('currentSelectedQueries', selectedQueries)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.TOGGLE_PREVIOUSLY_ASKED_QUESTION: {
            const { question, queryKey, correct, guidanceNotes, documentQueryId } = payload;
            const playgroundQuery: PlaygroundQuestion = { question, queryKey, correct, guidanceNotes, documentQueryId };
            let selectedQueries = state.currentSelectedQueries;
            const alreadyIncluded = selectedQueries.map(query => query.queryKey).includes(queryKey);
            if (alreadyIncluded) {
                selectedQueries = selectedQueries.filter(query => query.queryKey !== queryKey);
            } else {
                selectedQueries = [...selectedQueries, playgroundQuery];
            }
            return flow(
                set('selectedQueries', selectedQueries),
                set('currentSelectedQueries', selectedQueries)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.UPDATE_AI_PLAYGROUND_SEARCH:
            return set('aiPlaygroundSearchTerm', payload, state);
        case AdminAIPlaygroundActionTypes.UPDATE_SELECTED_DOCUMENT_QUERY: {
            const { index, key, value } = payload;
            return set(`currentSelectedQueries[${index}][${key}]`, value, state);
        }
        case AdminAIPlaygroundActionTypes.ADD_NEW_PLAYGROUND_DOCUMENT_QUERY: {
            const newPlaygroundQuestion: PlaygroundQuestion = { question: '', queryKey: '', guidanceNotes: '', correct: false };
            const updatedQueries = [...state.currentSelectedQueries, newPlaygroundQuestion];
            return set('currentSelectedQueries', updatedQueries, state);
        }
        case AdminAIPlaygroundActionTypes.DELETE_PLAYGROUND_DOCUMENT_QUERY: {
            // Set selectedQueries to updatedQueries as well to account for if a previously asked question had been selected and then deleted
            // This ensures it's removed from selectedQueries and therefore doesn't constantly trigger the confirmation dialog
            const updatedQueries = state.currentSelectedQueries.filter((_, index) => index !== payload);
            return flow(
                set('currentSelectedQueries', updatedQueries),
                set('selectedQueries', updatedQueries)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.DELETE_NEW_QUESTION_PLAYGROUND_QUERIES: {
            // Reset selectedQueries here to ensure we remove any selected previously asked questions. This is because we don't have a documentQueryId
            // for previously asked questions and can't filter them out. This ensures we don't continuously trigger the confirmation dialog
            const updatedQueries = state.currentSelectedQueries.filter(({ documentQueryId }) => !isUndefined(documentQueryId));
            return flow(
                set('currentSelectedQueries', updatedQueries),
                set('selectedQueries', updatedQueries)
            )(state);
        }
        case AdminAIPlaygroundActionTypes.TOGGLE_CONFIRMATION_MODAL:
            return set('confirmationModalVisible', payload, state);
        case AdminAIPlaygroundActionTypes.ASK_AI_PLAYGROUND_QUESTIONS_STARTED:
            return set('isAskingAIPlaygroundQuestions', true, state);
        case AdminAIPlaygroundActionTypes.ASK_AI_PLAYGROUND_QUESTIONS_SUCCESSFUL:
            return flow(
                set('isAskingAIPlaygroundQuestions', false),
                set('aiResults', payload.data),
                set('mlTestDocumentExtractId', payload.mlTestDocumentExtractId)
            )(state);
        case AdminAIPlaygroundActionTypes.ASK_AI_PLAYGROUND_QUESTIONS_FAILED:
            return set('isAskingAIPlaygroundQuestions', false, state);
        case AdminAIPlaygroundActionTypes.UPDATE_CORRECT_PLAYGROUND_QUERIES:
            return set('currentCorrectQueries', payload, state);
        case AdminAIPlaygroundActionTypes.SET_IS_FILTERING_SELECTED_QUERIES:
            return set('isFilteringQuerySelection', payload, state);
        case AdminAIPlaygroundActionTypes.RESET_SELECTED_QUERIES_RESULTS:
            return set('aiResults', null, state);
        case AdminAIPlaygroundActionTypes.RESET_SELECTED_QUERIES:
            return flow(
                set('selectedAIPlaygroundTab', AIPlaygroundTab.ACTIVE_AI_QUESTIONS),
                set('confirmationModalVisible', false),
                set('currentSelectedQueries', []),
                set('selectedQueries', []),
                set('aiResults', null),
                set('selectedPreviousQuestionsTab', PreviousQuestionsTab.CORRECT)
            )(state);
        case AdminAIPlaygroundActionTypes.SET_PLAYGROUND_ANSWER_HIGHLIGHT_INDEXES: {
            const { key, indexes, isFuzzyMatchHighlight } = payload;
            const updatedData = { ...state.aiResults![key], highlightIndexes: indexes, isFuzzyMatchHighlight };
            return set(`aiResults[${key}]`, updatedData, state);
        }
        case AdminAIPlaygroundActionTypes.SET_SELECTED_AI_PLAYGROUND_TAB:
            return set('selectedAIPlaygroundTab', payload, state);
        case AdminAIPlaygroundActionTypes.UPDATE_PLAYGROUND_TAB:
            return set('proposedAIPlaygroundTab', payload, state);
        case AdminAIPlaygroundActionTypes.FETCH_ALL_PREVIOUS_PLAYGROUND_QUESTIONS_AND_ANSWERS_STARTED:
            return flow(
                set('isFetchingAllPreviousQuestionsAndAnswers', true)
            )(state);
        case AdminAIPlaygroundActionTypes.FETCH_ALL_PREVIOUS_PLAYGROUND_QUESTIONS_AND_ANSWERS_SUCCESSFUL:
            return flow(
                set('isFetchingAllPreviousQuestionsAndAnswers', false),
                set('allPreviousQuestionsAndAnswers', payload)
            )(state);
        case AdminAIPlaygroundActionTypes.FETCH_ALL_PREVIOUS_PLAYGROUND_QUESTIONS_AND_ANSWERS_FAILED:
            return set('isFetchingAllPreviousQuestionsAndAnswers', false, state);
        case AdminAIPlaygroundActionTypes.UPDATE_AI_PREVIOUSLY_ASKED_QUERIES_SEARCH:
            return set('previouslyAskedQueriesSearchTerm', payload, state);
        case AdminAIPlaygroundActionTypes.SET_SELECTED_PREVIOUS_QUESTIONS_TAB:
            return set('selectedPreviousQuestionsTab', payload, state);
        case LoginActionTypes.LOGOUT_SUCCESSFUL:
            return INITIAL_STATE;
        default:
            return state;
    }
};
